import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Upload,
  Search,
  Download,
  Delete,
  Visibility,
  Description,
  Image,
  PictureAsPdf,
  InsertDriveFile,
  FilterList,
  CloudUpload,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';

import { useAuth } from '../contexts/AuthContext.tsx';
import { documentsApi, projectsApi } from '../services/api.ts';
import { Document, DocumentType } from '../types/index.ts';

// Upload document dialog component
interface UploadDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File, projectId: number, documentType: DocumentType, title?: string) => void;
}

const UploadDocumentDialog: React.FC<UploadDocumentDialogProps> = ({ open, onClose, onUpload }) => {
  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm();
  const { data: projects } = useQuery('projects', () => projectsApi.getProjects());
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const watchProjectId = watch('project_id');

  const onSubmit = (data: any) => {
    if (selectedFile) {
      onUpload(selectedFile, data.project_id, data.document_type, data.title);
      reset();
      setSelectedFile(null);
      onClose();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Document</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="project_id"
                control={control}
                rules={{ required: 'Project is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.project_id}>
                    <InputLabel>Project</InputLabel>
                    <Select {...field} label="Project">
                      {projects?.projects.map((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          {project.title} - {project.project_number}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="document_type"
                control={control}
                rules={{ required: 'Document type is required' }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.document_type}>
                    <InputLabel>Document Type</InputLabel>
                    <Select {...field} label="Document Type">
                      <MenuItem value={DocumentType.PHOTO}>Photo</MenuItem>
                      <MenuItem value={DocumentType.QUOTE}>Quote</MenuItem>
                      <MenuItem value={DocumentType.CONTRACT}>Contract</MenuItem>
                      <MenuItem value={DocumentType.SURVEY_REPORT}>Survey Report</MenuItem>
                      <MenuItem value={DocumentType.MCS_CERTIFICATE}>MCS Certificate</MenuItem>
                      <MenuItem value={DocumentType.DNO_APPLICATION}>DNO Application</MenuItem>
                      <MenuItem value={DocumentType.DNO_APPROVAL}>DNO Approval</MenuItem>
                      <MenuItem value={DocumentType.INSTALLATION_CERTIFICATE}>Installation Certificate</MenuItem>
                      <MenuItem value={DocumentType.WARRANTY}>Warranty</MenuItem>
                      <MenuItem value={DocumentType.USER_GUIDE}>User Guide</MenuItem>
                      <MenuItem value={DocumentType.INVOICE}>Invoice</MenuItem>
                      <MenuItem value={DocumentType.OTHER}>Other</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Document Title"
                    fullWidth
                    placeholder={selectedFile ? selectedFile.name : 'Enter document title...'}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  border: '2px dashed',
                  borderColor: selectedFile ? 'success.main' : 'grey.300',
                  backgroundColor: selectedFile ? 'success.light' : 'grey.50',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <input
                  id="file-input"
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                />
                
                <CloudUpload sx={{ fontSize: 48, color: selectedFile ? 'success.main' : 'text.secondary', mb: 1 }} />
                
                {selectedFile ? (
                  <Box>
                    <Typography variant="h6" color="success.main">
                      File Selected
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </Typography>
                    <Button variant="outlined" size="small" sx={{ mt: 1 }}>
                      Choose Different File
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="h6" color="text.secondary">
                      Click to upload file
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      PDF, DOC, DOCX, JPG, PNG files up to 10MB
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={!selectedFile || !watchProjectId}
          >
            Upload Document
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const DocumentsPage: React.FC = () => {
  const { isManager, isSurveyor, isCustomer } = useAuth();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<DocumentType | 'all'>('all');
  const [projectFilter, setProjectFilter] = useState<number | 'all'>('all');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // Fetch documents with filters
  const { data: documentsData, isLoading, error } = useQuery(
    ['documents', { 
      document_type: typeFilter !== 'all' ? typeFilter : undefined,
      project_id: projectFilter !== 'all' ? projectFilter : undefined 
    }],
    () => documentsApi.getDocuments({
      document_type: typeFilter !== 'all' ? typeFilter : undefined,
      project_id: projectFilter !== 'all' ? projectFilter : undefined,
    }),
    {
      refetchOnWindowFocus: false,
    }
  );

  // Fetch projects for filter dropdown
  const { data: projects } = useQuery('projects', () => projectsApi.getProjects());

  // Upload document mutation
  const uploadDocumentMutation = useMutation(
    ({ file, projectId, documentType, title }: { 
      file: File; 
      projectId: number; 
      documentType: DocumentType; 
      title?: string;
    }) => documentsApi.uploadDocument(projectId, file, documentType, title),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('documents');
      },
    }
  );

  // Delete document mutation
  const deleteDocumentMutation = useMutation(
    (documentId: number) => documentsApi.deleteDocument(documentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('documents');
      },
    }
  );

  const handleUploadDocument = (file: File, projectId: number, documentType: DocumentType, title?: string) => {
    uploadDocumentMutation.mutate({ file, projectId, documentType, title });
  };

  const handleDownloadDocument = async (documentId: number, filename: string) => {
    try {
      const blob = await documentsApi.downloadDocument(documentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleDeleteDocument = (documentId: number) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      deleteDocumentMutation.mutate(documentId);
    }
  };

  // Filter documents based on search term
  const filteredDocuments = React.useMemo(() => {
    if (!documentsData?.documents) return [];

    return documentsData.documents.filter((doc) =>
      doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.original_filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.document_type_display.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [documentsData?.documents, searchTerm]);

  const getDocumentIcon = (document: Document) => {
    if (document.is_image) {
      return <Image sx={{ fontSize: 32, color: 'info.main' }} />;
    } else if (document.is_pdf) {
      return <PictureAsPdf sx={{ fontSize: 32, color: 'error.main' }} />;
    } else {
      return <InsertDriveFile sx={{ fontSize: 32, color: 'text.secondary' }} />;
    }
  };

  const getTypeColor = (type: DocumentType) => {
    switch (type) {
      case DocumentType.MCS_CERTIFICATE:
        return 'success';
      case DocumentType.QUOTE:
        return 'info';
      case DocumentType.PHOTO:
        return 'warning';
      case DocumentType.CONTRACT:
        return 'primary';
      default:
        return 'default';
    }
  };

  if (error) {
    return (
      <Alert severity="error">
        Failed to load documents. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Documents
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {documentsData?.total || 0} total documents
          </Typography>
        </Box>
        {(isManager || isSurveyor) && (
          <Button
            variant="contained"
            startIcon={<Upload />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload Document
          </Button>
        )}
      </Box>

      {/* Upload Progress */}
      {uploadDocumentMutation.isLoading && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            Uploading document...
          </Typography>
          <LinearProgress />
        </Paper>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search documents by title or filename..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={typeFilter}
                label="Document Type"
                onChange={(e) => setTypeFilter(e.target.value as DocumentType | 'all')}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value={DocumentType.PHOTO}>Photos</MenuItem>
                <MenuItem value={DocumentType.QUOTE}>Quotes</MenuItem>
                <MenuItem value={DocumentType.SURVEY_REPORT}>Survey Reports</MenuItem>
                <MenuItem value={DocumentType.MCS_CERTIFICATE}>MCS Certificates</MenuItem>
                <MenuItem value={DocumentType.CONTRACT}>Contracts</MenuItem>
                <MenuItem value={DocumentType.INVOICE}>Invoices</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select
                value={projectFilter}
                label="Project"
                onChange={(e) => setProjectFilter(e.target.value as number | 'all')}
              >
                <MenuItem value="all">All Projects</MenuItem>
                {projects?.projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Documents Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Project</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 6 }).map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : filteredDocuments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 4 }}>
                    <Description sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      {searchTerm || typeFilter !== 'all' || projectFilter !== 'all' 
                        ? 'No documents found matching your criteria.' 
                        : 'No documents uploaded yet.'}
                    </Typography>
                    {(isManager || isSurveyor) && !searchTerm && typeFilter === 'all' && projectFilter === 'all' && (
                      <Button
                        variant="outlined"
                        startIcon={<Upload />}
                        onClick={() => setUploadDialogOpen(true)}
                        sx={{ mt: 2 }}
                      >
                        Upload First Document
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredDocuments.map((document) => (
                <TableRow key={document.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'transparent' }}>
                        {getDocumentIcon(document)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {document.title || document.original_filename}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {document.original_filename}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={document.document_type_display}
                      color={getTypeColor(document.document_type) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      Project {document.project_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {document.file_size_mb} MB
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(document.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View/Download">
                      <IconButton
                        size="small"
                        onClick={() => handleDownloadDocument(document.id, document.original_filename)}
                      >
                        <Download />
                      </IconButton>
                    </Tooltip>
                    
                    {(isManager || isSurveyor) && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteDocument(document.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Upload Document Dialog */}
      <UploadDocumentDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUpload={handleUploadDocument}
      />
    </Box>
  );
};

export default DocumentsPage;